import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const SECTIONS = [
  {
    title: "1. Acceptance of These Terms",
    body: "By listing any item, service, or custom commission for sale on the Call to Arms Bazaar (\"the Bazaar\"), you agree to these Bazaar Seller Terms & Conditions (\"these Terms\") in addition to the general Call to Arms Terms of Service. If you do not agree, do not list items for sale.",
  },
  {
    title: "2. What the Bazaar Is (and Isn't)",
    body: "Call to Arms, operated by Why Not? LTD (\"we\", \"us\", \"the Platform\"), provides the Bazaar as a marketplace facilitation tool only. We are not a party to the sale between you and any buyer. We do not manufacture, inspect, guarantee, or take ownership of anything you list. Payment processing is handled by Stripe Connect directly between you and the buyer; we never take a cut and never intend to.",
  },
  {
    title: "3. Accurate Listings",
    body: "Every listing must accurately represent what a buyer will actually receive - the title, description, preview image, price, and (for custom commissions) the scope of work you're offering. Selling something materially different from what you described, or failing to deliver what was paid for, is grounds for suspension or permanent ban (see Section 13).",
  },
  {
    title: "4. No Impersonation or False Affiliation",
    body: "You may never represent, imply, or suggest that you speak for, are employed by, are endorsed by, or otherwise officially represent Call to Arms or Why Not? LTD. You are an independent seller. Your storefront, listings, and any communication with buyers must make clear you are acting on your own behalf.",
  },
  {
    title: "5. No Platform Fees",
    body: "We do not charge sellers or buyers any fee for using the Bazaar, and we have no plans to start. If anyone claims to charge a fee on our behalf, or tells a buyer/seller that a fee is required by Call to Arms or Why Not? LTD, report it immediately - this is an automatic, severe violation of these Terms.",
  },
  {
    title: "6. Intellectual Property",
    body: "You retain full ownership of everything you create and list. By listing an item, you grant the Platform a limited, non-exclusive license to display its preview image, title, and description on the Bazaar for the purpose of facilitating your sale. You warrant that you own, or have full rights to sell, everything in your listing - listing content you don't have the rights to (art, music, copyrighted material, etc.) will be removed and may result in suspension or ban.",
  },
  {
    title: "7. Prohibited Content & Conduct",
    body: "The following are never allowed on the Bazaar: real-world illegal goods or services; malware or harmful files; content that infringes someone else's copyright or trademark; content depicting or sexualizing minors in any way; harassment, hate speech, or targeted abuse; scams, fraud, or any attempt to circumvent Stripe/platform safeguards.",
  },
  {
    title: "8. Adult Content Policy",
    body: "You may sell explicit/adult content, but every such listing must be checked/marked as adult at the time of creation. Adult-marked listings are automatically shown with a blurred preview on the Bazaar until a viewer chooses to reveal it. Mismarking adult content (failing to flag it) is a direct violation of these Terms and grounds for suspension or ban.",
  },
  {
    title: "9. Custom Commission Listings",
    body: "Listings marked as a custom commission are made-to-order - the deliverable does not exist yet at the time of listing. Buyers and sellers are expected to use the Contact Seller option to discuss scope, timeline, and details before proceeding. The Platform is not a party to, and does not mediate, custom-commission negotiations, revisions, or disputes between you and a buyer - you are responsible for delivering what you agree to.",
  },
  {
    title: "10. Taxes & Legal Compliance",
    body: "You are solely responsible for determining, collecting, and remitting any taxes owed on your sales, and for complying with all laws applicable to you and what you sell. The Platform provides no tax advice and takes no responsibility for your legal or tax compliance.",
  },
  {
    title: "11. No Warranty; Limitation of Liability",
    body: "The Bazaar is provided as-is. We make no warranty regarding the quality, legality, or fitness of anything listed by a seller. To the fullest extent permitted by law, Why Not? LTD and Call to Arms are not liable for any dispute, loss, or damage arising from a transaction, listing, or interaction between a buyer and a seller.",
  },
  {
    title: "12. Indemnification",
    body: "You agree to indemnify and hold harmless Why Not? LTD and Call to Arms from any claim, loss, or damage (including reasonable legal fees) arising from your listings, your conduct as a seller, or your violation of these Terms.",
  },
  {
    title: "13. Suspension & Termination",
    body: "We may suspend (temporarily hide your listings while you resolve an issue) or permanently ban (also blocking future re-onboarding) your seller account for: scamming or non-delivery, attempting to charge fees in our name, misrepresenting affiliation with Call to Arms or Why Not? LTD, mismarked adult content, listing prohibited content or content you don't have rights to, or a pattern of verified buyer complaints. Suspension is typically the first response to a first-time issue; severe or repeat violations may result in an immediate permanent ban and removal of your listings.",
  },
  {
    title: "14. Governing Law",
    body: "These Terms are governed by the laws of the State of Ohio, United States, without regard to conflict-of-law principles, and any dispute arising from these Terms shall be resolved in the courts of Ohio.",
  },
  {
    title: "15. Changes to These Terms",
    body: "We may update these Terms from time to time. Continuing to sell on the Bazaar after an update means you accept the revised Terms.",
  },
];

export default function SellerTermsPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#151613] text-[#f1e0c5] px-6 py-10" data-testid="seller-terms-page">
      <div className="max-w-2xl mx-auto space-y-8">
        <button data-testid="seller-terms-back-button" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-xs text-[#f1e0c5]/50 hover:text-[#D4AF37] transition-colors duration-200">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div>
          <h1 className="font-display text-4xl">Bazaar Seller Terms & Conditions</h1>
          <p className="text-xs text-[#f1e0c5]/40 mt-2">Effective for all Bazaar sellers - Why Not? LTD (Ohio, United States)</p>
        </div>
        <div className="space-y-6">
          {SECTIONS.map((s) => (
            <div key={s.title} data-testid={`seller-terms-section-${s.title.split(".")[0]}`}>
              <h2 className="font-display text-lg text-[#D4AF37] mb-1.5">{s.title}</h2>
              <p className="text-sm text-[#f1e0c5]/70 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#f1e0c5]/30 pt-4 border-t border-white/5">
          Questions about these Terms? Reach out through your account support channel. Also see our{" "}
          <Link to="/legal/buyer-terms" data-testid="seller-terms-buyer-terms-link" className="text-[#D4AF37] underline">Buyer Terms</Link>.
        </p>
      </div>
    </div>
  );
}
